import {
  Fragment,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton, Tooltip, makeStyles } from '@material-ui/core';
import { IconComponent } from '@backstage/core-plugin-api';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import MapIcon from '@material-ui/icons/MyLocation';
import HealthAndSafetyIcon from '@material-ui/icons/LocalHospital';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import DataUsageIcon from '@material-ui/icons/DataUsage';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AssignmentIcon from '@material-ui/icons/Assignment';
import RouterIcon from '@material-ui/icons/Router';
import DnsIcon from '@material-ui/icons/Dns';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import ChatIcon from '@material-ui/icons/Chat';
import TimelineIcon from '@material-ui/icons/Timeline';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import StoreIcon from '@material-ui/icons/Store';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import CategoryIcon from '@material-ui/icons/Category';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const NAV_HIDDEN_KEY = 'backstage.sidenav.hidden';
// A thin rail keeps the toggle + menu icons off the page content (so they never
// overlap the heading) while still reclaiming most of the sidebar's width.
const RAIL_WIDTH = 48;

/**
 * The routable menu items, shared by the full sidebar and the collapsed icon
 * rail so the two can't drift. (Special items — search, my-groups,
 * notifications, settings — stay declared inline in the full Sidebar below.)
 */
const NAV_ITEMS: { icon: IconComponent; to: string; text: string }[] = [
  { icon: HomeIcon, to: '/', text: 'Home' },
  { icon: SearchIcon, to: 'search', text: 'Search' },
  { icon: ChatIcon, to: 'assistant/general', text: 'Assistant' },
  { icon: CategoryIcon, to: 'catalog', text: 'Catalog' },
  { icon: CreateComponentIcon, to: 'create', text: 'Software Templates' },
  { icon: ExtensionIcon, to: 'api-docs', text: 'APIs' },
  { icon: LibraryBooks, to: 'docs', text: 'Docs' },
  { icon: MapIcon, to: 'tech-radar', text: 'Tech Radar' },
  { icon: HealthAndSafetyIcon, to: 'openhmis', text: 'Open HMIS' },
  { icon: CloudQueueIcon, to: 'dhis2', text: 'DHIS2' },
  { icon: DataUsageIcon, to: 'openeverest', text: 'OpenEverest' },
  { icon: AccountTreeIcon, to: 'airflow', text: 'Airflow' },
  { icon: AssignmentIcon, to: 'asset-register', text: 'Asset Register' },
  { icon: RouterIcon, to: 'ipam', text: 'IPAM' },
  { icon: DnsIcon, to: 'proxmox', text: 'Proxmox' },
  // Standalone Kubernetes clusters page (packages/app/.../kubernetes).
  { icon: DeviceHubIcon, to: 'kubernetes', text: 'Kubernetes' },
  { icon: TimelineIcon, to: 'monitoring', text: 'Monitoring' },
  { icon: VerifiedUserIcon, to: 'audit', text: 'Audit' },
  { icon: StoreIcon, to: 'odoo', text: 'Odoo' },
  { icon: VerifiedUserIcon, to: 'rbac', text: 'Administration' },
];

const useToggleStyles = makeStyles(theme => ({
  rail: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: RAIL_WIDTH,
    zIndex: 1250,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  railDivider: {
    width: '60%',
    height: 1,
    margin: theme.spacing(1, 0),
    backgroundColor: theme.palette.divider,
    flexShrink: 0,
  },
  railLink: {
    display: 'flex',
    flexShrink: 0,
    color: 'inherit',
  },
  railIconActive: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
}));

/** A single icon link in the collapsed rail, with tooltip + active highlight. */
const RailItem = ({
  item,
  classes,
}: {
  item: { icon: IconComponent; to: string; text: string };
  classes: { railLink: string; railIconActive: string };
}) => {
  const { pathname } = useLocation();
  const path = item.to === '/' ? '/' : `/${item.to.replace(/^\/+/, '')}`;
  const active = item.to === '/' ? pathname === '/' : pathname.startsWith(path);
  return (
    <Tooltip title={item.text} placement="right">
      <Link to={item.to} aria-label={item.text} className={classes.railLink}>
        <IconButton
          component="span"
          size="medium"
          className={active ? classes.railIconActive : undefined}
        >
          <item.icon fontSize="small" />
        </IconButton>
      </Link>
    </Tooltip>
  );
};

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const toggleClasses = useToggleStyles();
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(NAV_HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleNav = () =>
    setHidden(prev => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_HIDDEN_KEY, next ? '1' : '0');
      } catch {
        // localStorage unavailable; the toggle still works for this session
      }
      return next;
    });

  // SidebarPage's root Box always reserves `padding-left` for the sidebar rail.
  // When the nav is hidden, override that padding directly on the DOM node so the
  // content fills the reclaimed space. The sentinel below is a child of that Box,
  // so its parentElement is exactly the padded element (class names are hashed in
  // production, so a CSS selector can't target it reliably).
  const sentinelRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const root = sentinelRef.current?.parentElement;
    if (!root) return undefined;
    root.style.paddingLeft = hidden ? `${RAIL_WIDTH}px` : '';
    return () => {
      root.style.paddingLeft = '';
    };
  }, [hidden]);

  return (
    <SidebarPage>
      <span ref={sentinelRef} style={{ display: 'none' }} />
      {hidden ? (
        <div className={toggleClasses.rail}>
          <Tooltip title="Show menu" placement="right">
            <IconButton aria-label="Show menu" onClick={toggleNav}>
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <div className={toggleClasses.railDivider} />
          {NAV_ITEMS.map(item => (
            <RailItem key={item.text} item={item} classes={toggleClasses} />
          ))}
        </div>
      ) : (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {/* Global nav, not org-specific */}
            <SidebarItem
              icon={ChevronLeftIcon}
              text="Hide menu"
              onClick={toggleNav}
            />
            {NAV_ITEMS.map(item => (
              <Fragment key={item.text}>
                <SidebarItem icon={item.icon} to={item.to} text={item.text} />
                {item.to === 'catalog' && (
                  <MyGroupsSidebarItem
                    singularTitle="My Group"
                    pluralTitle="My Groups"
                    icon={GroupIcon}
                  />
                )}
              </Fragment>
            ))}
            {/* End global nav */}
            <SidebarDivider />
            <SidebarScrollWrapper>
              {/* Items in this group will be scrollable if they run out of space */}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NotificationsSidebarItem />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
          </SidebarGroup>
        </Sidebar>
      )}
      {children}
    </SidebarPage>
  );
};
